import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { usePrefersReducedMotion } from '../lib/useReducedMotion';

const SECTIONS = ['home', 'about', 'skills', 'projects', 'experience', 'contact'] as const;
type SectionId = (typeof SECTIONS)[number];

const Header: React.FC = () => {
  const { t } = useTranslation();
  const reduce = usePrefersReducedMotion();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [active, setActive] = useState<SectionId>('home');

  const navItems: { id: SectionId; name: string }[] = [
    { id: 'home', name: t('nav.home') },
    { id: 'about', name: t('nav.about') },
    { id: 'skills', name: t('nav.skills') },
    { id: 'projects', name: t('nav.projects') },
    { id: 'experience', name: t('nav.experience') },
    { id: 'contact', name: t('nav.contact') },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll-spy: highlight the section currently in view.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id as SectionId);
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: [0, 0.25, 0.5, 1] }
    );
    SECTIONS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = useCallback(
    (id: SectionId) => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    },
    [reduce]
  );

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 bg-paper transition-colors duration-300 ${
        isScrolled || isMobileMenuOpen ? 'border-b border-hairline' : 'border-b border-transparent'
      }`}
    >
      <div className="container-page">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Wordmark. No monogram tile, no gradient — the name set in the display
              face at UI scale is the mark. */}
          <button
            onClick={() => scrollToSection('home')}
            className="ui flex min-h-[44px] shrink-0 items-center text-[15px] font-semibold tracking-[0.06em] text-ink"
            aria-label="Anas Jammal — home"
          >
            Anas Jammal
          </button>

          <nav className="hidden items-center gap-7 md:flex" aria-label="Primary">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                aria-current={active === item.id ? 'true' : undefined}
                className={`ui relative flex min-h-[44px] items-center text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors duration-200 ${
                  active === item.id ? 'text-ink' : 'text-graphite hover:text-ink'
                }`}
              >
                {item.name}
                {/* 1px, lacquer-bright. The bright step is load-bearing at this
                    weight: at 1px on paper, #0C4A38 reads as a thin dark rule
                    rather than as the accent. */}
                {active === item.id && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-2.5 h-px bg-lacquer-bright"
                  />
                )}
              </button>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => scrollToSection('contact')}
              className="btn-primary hidden md:inline-flex"
            >
              {t('nav.cta')}
            </button>

            <button
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="flex h-11 w-11 items-center justify-center text-ink md:hidden"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
            <nav id="mobile-nav" aria-label="Primary" className="md:hidden">
              <div className="flex flex-col pb-5 pt-2">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    aria-current={active === item.id ? 'true' : undefined}
                    className={`ui flex min-h-[44px] items-center text-left text-[13px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                      active === item.id ? 'text-ink' : 'text-graphite'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
                <button
                  onClick={() => scrollToSection('contact')}
                  className="btn-primary mt-4 w-full"
                >
                  {t('nav.cta')}
                </button>
              </div>
            </nav>
          )}
      </div>
    </header>
  );
};

export default Header;
